/**
 * Tab Patients nell'Admin Panel.
 * Lista pazienti con dettaglio, storico e merge duplicati.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { patientsApi, PatientDetail, PatientHistory, MergePatientsRequest, PatientCreate, PatientUpdate } from '../../api/patients'
import { ownersApi } from '../../api/owners'
import { SkeletonList, SkeletonText } from '../../components/Skeleton'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { UserPlus, Pencil } from 'lucide-react'

type PatientsView = 'list' | 'detail' | 'history'

export function PatientsTab() {
  const [view, setView] = useState<PatientsView>('list')
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('')
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [mergeSource, setMergeSource] = useState<string>('')
  const [mergeTarget, setMergeTarget] = useState<string>('')
  const [showCreate, setShowCreate] = useState(false)
  const [editingPatient, setEditingPatient] = useState<PatientDetail | null>(null)

  const queryClient = useQueryClient()

  const { data: owners } = useQuery({
    queryKey: ['owners-list'],
    queryFn: () => ownersApi.list(200),
    staleTime: 5 * 60 * 1000,
  })

  const { data: patients, isLoading: patientsLoading } = useQuery({
    queryKey: ['patients-list', selectedOwnerId, search],
    queryFn: () => patientsApi.list(selectedOwnerId || undefined, search || undefined, 200),
    enabled: view === 'list',
  })

  const { data: patientDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['patient-detail', selectedPatientId],
    queryFn: () => patientsApi.get(selectedPatientId!),
    enabled: view === 'detail' && !!selectedPatientId,
  })

  const { data: patientHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['patient-history', selectedPatientId],
    queryFn: () => patientsApi.getHistory(selectedPatientId!),
    enabled: view === 'history' && !!selectedPatientId,
  })

  const mergeMutation = useMutation({
    mutationFn: (req: MergePatientsRequest) => patientsApi.merge(req),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['patients-list'] })
      queryClient.invalidateQueries({ queryKey: ['owners-list'] })
      setShowMergeDialog(false)
      alert(`Merge completato: ${data.transferred_cases} casi trasferiti`)
    },
    onError: () => {
      alert('Errore durante il merge')
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: PatientCreate) => patientsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients-list'] })
      setShowCreate(false)
      setCreateForm({ name: '', species: '', breed: '', birth_date: '', gender: '', notes: '', owner_id: '' })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ patientId, data }: { patientId: string; data: PatientUpdate }) =>
      patientsApi.update(patientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients-list'] })
      queryClient.invalidateQueries({ queryKey: ['patient-detail', selectedPatientId] })
      setEditingPatient(null)
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const handleMerge = () => {
    if (!mergeSource || !mergeTarget) {
      alert('Seleziona entrambi i pazienti')
      return
    }
    if (mergeSource === mergeTarget) {
      alert('I pazienti devono essere diversi')
      return
    }
    mergeMutation.mutate({
      source_patient_id: mergeSource,
      target_patient_id: mergeTarget,
    })
  }

  const handleSelectPatient = (id: string) => {
    setSelectedPatientId(id)
    setView('detail')
  }

  const [createForm, setCreateForm] = useState<PatientCreate>({
    name: '', species: '', breed: '', birth_date: '', gender: '', notes: '', owner_id: '',
  })

  const [editForm, setEditForm] = useState<PatientUpdate>({})

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.name.trim() || !createForm.owner_id) {
      alert('Nome e proprietario sono obbligatori')
      return
    }
    createMutation.mutate(createForm)
  }

  const handleEdit = (patient: PatientDetail) => {
    setEditingPatient(patient)
    setEditForm({
      name: patient.name,
      species: patient.species || '',
      breed: patient.breed || '',
      birth_date: patient.birth_date || '',
      gender: patient.gender || '',
      notes: patient.notes || '',
      owner_id: patient.owner_id,
    })
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingPatient) return
    updateMutation.mutate({ patientId: editingPatient.id, data: editForm })
  }

  if (view === 'detail' && patientDetail) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Breadcrumb items={[
            { label: 'Pazienti', href: '' },
            { label: patientDetail.name },
          ]} />
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={() => handleEdit(patientDetail)}>
              <Pencil className="size-3.5 mr-1.5" />
              Modifica
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setView('history')} className="text-primary">
              Vedi storico
            </Button>
          </div>
        </div>

        {detailLoading ? (
          <SkeletonText lines={5} />
        ) : (
          <>
            {/* Patient Info */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">
                  {patientDetail.name}
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Specie:</span>{' '}
                    <span className="font-medium">{patientDetail.species || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Razza:</span>{' '}
                    <span className="font-medium">{patientDetail.breed || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Genere:</span>{' '}
                    <span className="font-medium">{patientDetail.gender || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Data di nascita:</span>{' '}
                    <span className="font-medium">
                      {patientDetail.birth_date ? new Date(patientDetail.birth_date).toLocaleDateString('it-IT') : '-'}
                    </span>
                  </div>
                </div>
                {patientDetail.notes && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-muted-foreground text-sm">Note:</span>{' '}
                    <span className="text-sm">{patientDetail.notes}</span>
                  </div>
                )}

                {/* Owner Link */}
                <div className="mt-4 pt-4 border-t border-border">
                  <span className="text-muted-foreground text-sm">Proprietario:</span>{' '}
                  <span className="text-sm font-medium">
                    {patientDetail.owner_name || patientDetail.owner_email}
                  </span>
                  {patientDetail.owner_phone && (
                    <span className="text-sm text-muted-foreground ml-2">
                      ({patientDetail.owner_phone})
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Cases */}
            <h3 className="text-md font-semibold text-foreground mb-3">
              Casi ({patientDetail.cases.length})
            </h3>
            {patientDetail.cases.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground text-sm">
                  Nessun caso associato
                </CardContent>
              </Card>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titolo</TableHead>
                      <TableHead>Specialità</TableHead>
                      <TableHead>Priorità</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Aperto il</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patientDetail.cases.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.title || '-'}</TableCell>
                        <TableCell>{c.specialty || '-'}</TableCell>
                        <TableCell>
                          {c.priority ? (
                            <Badge variant="outline" className={
                              c.priority === 'high' ? 'bg-danger/10 text-danger border-danger/20' :
                              c.priority === 'medium' ? 'bg-warning/10 text-warning border-warning/20' :
                              'bg-success/10 text-success border-success/20'
                            }>
                              {c.priority}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            c.status === 'open'
                              ? 'bg-success/10 text-success border-success/20'
                              : 'bg-muted text-muted-foreground border-border'
                          }>
                            {c.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.opened_at ? new Date(c.opened_at).toLocaleDateString('it-IT') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  if (view === 'history' && patientHistory) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Breadcrumb items={[
            { label: 'Pazienti', href: '' },
            { label: patientHistory.patient.name, href: '' },
            { label: 'Storico' },
          ]} />
        </div>

        {historyLoading ? (
          <SkeletonText lines={10} />
        ) : (
          <>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Storico — {patientHistory.patient.name} ({patientHistory.patient.species})
            </h2>

            {patientHistory.owner && (
              <Card className="mb-4">
                <CardContent className="p-4 text-sm">
                  <span className="text-muted-foreground">Proprietario:</span>{' '}
                  <span className="font-medium">{patientHistory.owner.full_name || patientHistory.owner.email}</span>
                  {patientHistory.owner.phone && (
                    <span className="text-muted-foreground ml-2">({patientHistory.owner.phone})</span>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Email Totali</div>
                <div className="text-2xl font-bold text-success">{patientHistory.total_emails}</div>
              </CardContent>
            </Card>

            {patientHistory.cases.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground text-sm">
                  Nessun caso nello storico
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {patientHistory.cases.map((c) => (
                  <Card key={c.id}>
                    <div className="px-4 py-3 bg-muted/30 border-b border-border flex justify-between">
                      <h3 className="text-sm font-semibold text-foreground">
                        {c.title || 'Senza titolo'} — {c.specialty || '-'}
                      </h3>
                      <Badge variant="outline" className={
                        c.status === 'open'
                          ? 'bg-success/10 text-success border-success/20'
                          : 'bg-muted text-muted-foreground border-border'
                      }>
                        {c.status}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      {c.threads.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nessun thread</p>
                      ) : (
                        <div className="space-y-2">
                          {c.threads.map((t) => (
                            <div key={t.id} className="flex justify-between text-sm py-2 border-b border-border last:border-0">
                              <span className="font-medium">{t.subject || 'Senza oggetto'}</span>
                              <span className="text-muted-foreground text-xs">
                                {t.emails?.length || 0} email
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // LIST VIEW
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Pazienti</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreate(true)}>
            <UserPlus className="size-4 mr-2" />
            Aggiungi Paziente
          </Button>
          <Button variant="outline" onClick={() => setShowMergeDialog(true)}>
            Merge Duplicati
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca paziente, proprietario, specie..."
          className="flex-1"
        />
        <Select value={selectedOwnerId || undefined} onValueChange={(v) => setSelectedOwnerId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tutti i proprietari" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i proprietari</SelectItem>
            {owners?.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.full_name || o.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {patientsLoading ? (
        <SkeletonList count={5} />
      ) : patients?.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            {search || selectedOwnerId ? 'Nessun risultato per i filtri' : 'Nessun paziente trovato'}
          </CardContent>
        </Card>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Specie</TableHead>
                <TableHead>Razza</TableHead>
                <TableHead>Proprietario</TableHead>
                <TableHead>Casi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients?.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSelectPatient(p.id)}
                >
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.species || '-'}</TableCell>
                  <TableCell>{p.breed || '-'}</TableCell>
                  <TableCell>{p.owner_name || '-'}</TableCell>
                  <TableCell>
                    <Badge>{p.cases_count}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Pazienti Duplicati</DialogTitle>
            <DialogDescription>
              Seleziona il paziente da eliminare (source) e quello che riceverà i casi (target).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Paziente Source (da eliminare)</Label>
              <Select value={mergeSource} onValueChange={setMergeSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {patients?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.species}) — {p.owner_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paziente Target (ricevente)</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {patients?.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={p.id === mergeSource}>
                      {p.name} ({p.species}) — {p.owner_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {mergeMutation.isError && (
            <p className="text-danger text-sm">
              Errore: {(mergeMutation.error as any)?.response?.data?.detail || 'Impossibile eseguire il merge'}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMergeDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleMerge} disabled={mergeMutation.isPending}>
              {mergeMutation.isPending ? 'Merge...' : 'Esegui Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Paziente</DialogTitle>
            <DialogDescription>Crea un nuovo paziente manualmente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Specie</Label>
                <Input value={createForm.species} onChange={(e) => setCreateForm({ ...createForm, species: e.target.value })} placeholder="Cane, Gatto..." />
              </div>
              <div>
                <Label>Razza</Label>
                <Input value={createForm.breed} onChange={(e) => setCreateForm({ ...createForm, breed: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Genere</Label>
                <Select value={createForm.gender} onValueChange={(v) => setCreateForm({ ...createForm, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data di nascita</Label>
                <Input type="date" value={createForm.birth_date} onChange={(e) => setCreateForm({ ...createForm, birth_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Proprietario *</Label>
              <Select value={createForm.owner_id} onValueChange={(v) => setCreateForm({ ...createForm, owner_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleziona proprietario..." /></SelectTrigger>
                <SelectContent>
                  {owners?.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.full_name || o.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creazione...' : 'Crea Paziente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingPatient} onOpenChange={(open) => !open && setEditingPatient(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Paziente</DialogTitle>
            <DialogDescription>{editingPatient?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Specie</Label>
                <Input value={editForm.species || ''} onChange={(e) => setEditForm({ ...editForm, species: e.target.value })} />
              </div>
              <div>
                <Label>Razza</Label>
                <Input value={editForm.breed || ''} onChange={(e) => setEditForm({ ...editForm, breed: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Genere</Label>
                <Select value={editForm.gender || ''} onValueChange={(v) => setEditForm({ ...editForm, gender: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschio</SelectItem>
                    <SelectItem value="F">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data di nascita</Label>
                <Input type="date" value={editForm.birth_date || ''} onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingPatient(null)}>Annulla</Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
